import { Container, Graphics, Sprite, Texture } from 'pixi.js';
import type { AABB } from '../engine/Physics';
import type { MovingPlatformDef } from '../types';

const COLOR_OUTLINE = 0x000000;

function hex(color: string): number {
  return parseInt(color.replace('#', ''), 16);
}

/**
 * Animated platform that oscillates linearly along one axis. The AABB is the
 * authoritative collision rect — Game keeps a reference to it inside `solids`
 * and we mutate it in place each step.
 */
export class MovingPlatform {
  aabb: AABB;
  gfx = new Container();
  private graphics = new Graphics();

  prevX: number;
  prevY: number;

  private origX: number;
  private origY: number;
  private axis: 'x' | 'y';
  private range: number;
  private speed: number;
  private offset = 0;
  private dir: 1 | -1 = 1;

  constructor(def: MovingPlatformDef, texUrl?: string) {
    this.aabb = { x: def.x, y: def.y, w: def.w, h: def.h };
    this.origX = def.x;
    this.origY = def.y;
    this.axis = def.moveAxis;
    this.range = def.moveRange;
    this.speed = def.moveSpeed;
    this.prevX = def.x;
    this.prevY = def.y;

    if (texUrl) {
      const tex = Texture.from(texUrl);
      const sprite = new Sprite(tex);
      sprite.width = def.w;
      sprite.height = def.h;
      this.gfx.addChild(sprite);
    } else {
      this.graphics
        .rect(0, 0, def.w, def.h)
        .fill({ color: hex(def.color) })
        .stroke({ color: COLOR_OUTLINE, width: 2, alignment: 1 });
      this.gfx.addChild(this.graphics);
    }
    this.gfx.x = def.x;
    this.gfx.y = def.y;
  }

  step(): void {
    this.prevX = this.aabb.x;
    this.prevY = this.aabb.y;

    this.offset += this.speed * this.dir;
    if (this.offset >= this.range) {
      this.offset = this.range;
      this.dir = -1;
    } else if (this.offset <= -this.range) {
      this.offset = -this.range;
      this.dir = 1;
    }

    if (this.axis === 'x') {
      this.aabb.x = this.origX + this.offset;
    } else {
      this.aabb.y = this.origY + this.offset;
    }

    this.gfx.x = this.aabb.x;
    this.gfx.y = this.aabb.y;
  }

  deltaX(): number {
    return this.aabb.x - this.prevX;
  }

  deltaY(): number {
    return this.aabb.y - this.prevY;
  }
}
